import {
  ComponentType,
  createElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { Divider } from "@magnetic/divider";

import { logger } from "src/core/globals";
import { LoginContext } from "src/core/loginContext";
import { ThemeContext } from "src/core/themeContext";
import {
  getC3Context,
  getLocalToC3Theme,
  isValidAIAssistantConfig,
  isValidC3Context,
  persistTheme
} from "src/utils/c3Utils";
import { getModuleFederation } from "src/utils/mfRuntime";
import { getUserLocale } from "src/utils/timeZone/timeZoneUtils";

interface TopNavigationProps {
  mode: "hosted";
  theme: string;
  env: string;
  accessToken: string;
  activeProductId: string;
  skipThemeProvider: boolean;
}

interface TenantPickerProps {
  env: string;
  theme: string;
  email?: string;
  accessToken: string;
  skipThemeProvider: boolean;
}

interface HeaderLogoProps {
  mode: "hosted";
  theme: string;
  env: string;
  accessToken: string;
  skipThemeProvider: boolean;
}

interface AiCanvasLauncherProps {
  mode: "hosted";
  env: string;
  theme: string;
  accessToken: string;
  activeProductId: string;
  skipThemeProvider: boolean;
}

type AiAssistantConfig = {
  locale: string;
  appTheme: string;
  colorTheme: string;
  edgeApiUrl: string;
  launchButtonLabel: string;
  auth: Record<string, string>;
  localStorageKeyPrefix: string;
};

type AiAssistantElement = HTMLElement & {
  config?: AiAssistantConfig;
};

type TopNavigationRemoteModule = {
  default: ComponentType<TopNavigationProps>;
};

type TenantPickerRemoteModule = {
  default: ComponentType<TenantPickerProps>;
};

type HeaderLogoRemoteModule = {
  default: ComponentType<HeaderLogoProps>;
};

type AiCanvasLauncherRemoteModule = {
  default: ComponentType<AiCanvasLauncherProps>;
};

type C3HeaderRemoteModuleMap = {
  AiCanvasLauncher: AiCanvasLauncherRemoteModule;
  HeaderLogo: HeaderLogoRemoteModule;
  TopNavigation: TopNavigationRemoteModule;
  TenantPicker: TenantPickerRemoteModule;
};

function registerC3HeaderRemote(baseUrl: string) {
  // Ensure MF runtime is initialized before any remote is loaded
  const mf = getModuleFederation();

  // Register the remote before loading the module.
  // This is necessary for MF to know where to load the remote from.
  mf.registerRemotes([
    {
      name: "cccUnifiedHeader",
      entry: `${baseUrl}/mfes/ccc-unified-header/latest/remoteEntry.js`
    }
  ]);
}

function loadC3HeaderModule<TModule extends keyof C3HeaderRemoteModuleMap>(
  moduleName: TModule
): Promise<C3HeaderRemoteModuleMap[TModule]> {
  const mf = getModuleFederation();

  // Now load the remote module
  return mf.loadRemote(`cccUnifiedHeader/${moduleName}`) as Promise<
    C3HeaderRemoteModuleMap[TModule]
  >;
}

export default function C3Header() {
  const [HeaderLogo, setHeaderLogo] =
    useState<ComponentType<HeaderLogoProps> | null>(null);
  const [TopNav, setTopNav] =
    useState<ComponentType<TopNavigationProps> | null>(null);
  const [TenantPicker, setTenantPicker] =
    useState<ComponentType<TenantPickerProps> | null>(null);
  const c3Context = useMemo(getC3Context, []);
  const { theme } = useContext(ThemeContext);
  const { session, logout } = useContext(LoginContext);
  const hasThemeChangedSinceLaunch = useRef(false);
  const [c3Theme, setC3Theme] = useState<string>(c3Context.theme);

  useEffect(() => {
    if (!session?.isC3Crosslaunch || !isValidC3Context(c3Context)) {
      // This component is meant to be only for C3 users.
      // Stop and log out if this component is called outside C3 flow.
      // Do the same if the C3 context is not valid.
      //
      // Invalid C3 context is possible if the user navigated
      // to a Hyperfabric via C3 launch then opens a new tab and hits
      // Hyperfabric directly.
      //
      // In this case, the user session token from the C3 session
      // is still valid and reused but C3 context is not present
      // So log the user out to clear any potentially stale/invalid
      // session and prompt them to login again.
      logger.error("Invalid C3 user or context.");
      logout();
      return;
    }

    // Register the C3 header remote before loading any modules from it.
    registerC3HeaderRemote(c3Context.baseUrl);

    Promise.allSettled([
      loadC3HeaderModule("HeaderLogo"),
      loadC3HeaderModule("TenantPicker"),
      loadC3HeaderModule("TopNavigation")
    ]).then(([headerLogoResult, tenantPickerResult, topNavResult]) => {
      if (headerLogoResult.status === "fulfilled") {
        setHeaderLogo(() => headerLogoResult.value.default);
      } else {
        logger.error(headerLogoResult.reason);
      }

      if (tenantPickerResult.status === "fulfilled") {
        setTenantPicker(() => tenantPickerResult.value.default);
      } else {
        logger.error(tenantPickerResult.reason);
      }

      if (topNavResult.status === "fulfilled") {
        setTopNav(() => topNavResult.value.default);
      } else {
        logger.error(topNavResult.reason);
      }
    });
  }, [c3Context, session, logout]);

  useEffect(() => {
    // hasThemeChangedSinceLaunch exists is to preserve this distinction:
    //   - initial C3 launch theme: may be "system"
    //   - current local app theme: only "light" or "dark"
    if (!hasThemeChangedSinceLaunch.current) {
      hasThemeChangedSinceLaunch.current = true;
      return;
    }
    const c3Theme = getLocalToC3Theme(theme);
    persistTheme(c3Theme);
    setC3Theme(c3Theme);
  }, [theme]);

  return (
    <>
      {HeaderLogo && (
        <div className="c3-header-logo">
          <HeaderLogo
            mode="hosted"
            theme={c3Theme}
            env={c3Context.env}
            accessToken={c3Context.accessToken}
            skipThemeProvider={true}
          />
        </div>
      )}
      <div className="c3-header-divider" data-cds-theme="magnetic-blue-dark">
        <Divider
          className="mds-header-alert-divider"
          direction="vertical"
          size="xs"
          strong={true}
        />
      </div>
      {TenantPicker && (
        <div className="c3-header-tenant-picker">
          <TenantPicker
            theme={c3Theme}
            env={c3Context.env}
            email={session?.email}
            accessToken={c3Context.accessToken}
            skipThemeProvider={true}
          />
        </div>
      )}
      {TopNav && (
        <div className="c3-header-top-navigation">
          <TopNav
            mode="hosted"
            env={c3Context.env}
            theme={c3Theme}
            accessToken={c3Context.accessToken}
            activeProductId="hyperfabric"
            skipThemeProvider={true}
          />
        </div>
      )}
    </>
  );
}

export function C3AICanvas() {
  const [AiCanvasLauncher, setAiCanvasLauncher] =
    useState<ComponentType<AiCanvasLauncherProps> | null>(null);
  const c3Context = useMemo(getC3Context, []);
  const { theme } = useContext(ThemeContext);
  const { session, logout } = useContext(LoginContext);
  const hasThemeChangedSinceLaunch = useRef(false);
  const [c3Theme, setC3Theme] = useState<string>(c3Context.theme);

  useEffect(() => {
    if (!session?.isC3Crosslaunch || !isValidC3Context(c3Context)) {
      logger.error("Invalid C3 user or context.");
      logout();
      return;
    }

    registerC3HeaderRemote(c3Context.baseUrl);

    loadC3HeaderModule("AiCanvasLauncher")
      .then((aiCanvasLauncherResult) => {
        setAiCanvasLauncher(() => aiCanvasLauncherResult.default);
      })
      .catch((error) => {
        logger.error(error);
      });
  }, [c3Context, session, logout]);

  useEffect(() => {
    // hasThemeChangedSinceLaunch exists is to preserve this distinction:
    //   - initial C3 launch theme: may be "system"
    //   - current local app theme: only "light" or "dark"
    if (!hasThemeChangedSinceLaunch.current) {
      hasThemeChangedSinceLaunch.current = true;
      return;
    }
    const c3Theme = getLocalToC3Theme(theme);
    persistTheme(c3Theme);
    setC3Theme(c3Theme);
  }, [theme]);

  return (
    <>
      {AiCanvasLauncher && (
        <AiCanvasLauncher
          mode="hosted"
          env={c3Context.env}
          theme={c3Theme}
          accessToken={c3Context.accessToken}
          activeProductId="hyperfabric"
          skipThemeProvider={true}
        />
      )}
    </>
  );
}

const AI_ASSISTANT_ELEMENT_NAME = "ai-assistant";
const loadedAiAssistantScripts = new Map<string, Promise<void>>();

// AI Assistant is exposed as a custom element script, not a Module Federation
// React component. Keep the script load idempotent across remounts.
function loadAiAssistantScript(remoteEntryUrl: string): Promise<void> {
  if (customElements.get(AI_ASSISTANT_ELEMENT_NAME)) {
    return Promise.resolve();
  }

  const loadedScript = loadedAiAssistantScripts.get(remoteEntryUrl);
  if (loadedScript) {
    return loadedScript;
  }

  const script = document.createElement("script");
  script.async = true;
  script.src = remoteEntryUrl;

  const loadPromise = new Promise<void>((resolve, reject) => {
    script.onload = () => {
      resolve();
    };
    script.onerror = () => {
      loadedAiAssistantScripts.delete(remoteEntryUrl);
      reject(new Error(`Unable to load ${remoteEntryUrl}`));
    };
  });

  loadedAiAssistantScripts.set(remoteEntryUrl, loadPromise);
  document.body.appendChild(script);

  return loadPromise;
}

export function C3AIAssistant() {
  const ref = useRef<AiAssistantElement | null>(null);
  const c3Context = useMemo(getC3Context, []);
  const { theme } = useContext(ThemeContext);
  const aiAssistantTheme = getLocalToC3Theme(theme);
  const remoteEntryUrl = c3Context.aiAssistantEntryUrl;
  const isConfigValid = isValidAIAssistantConfig(c3Context);

  useEffect(() => {
    if (!isConfigValid) {
      return;
    }

    const applyConfig = () => {
      // The AI Assistant integration contract expects config as a DOM property.
      if (!ref.current || !customElements.get(AI_ASSISTANT_ELEMENT_NAME)) {
        return;
      }

      ref.current.config = {
        appTheme: "HYPERFABRIC",
        colorTheme: aiAssistantTheme,
        edgeApiUrl: c3Context.aiAssistantBaseUrl,
        localStorageKeyPrefix: "aiAssistant.hyperfabric",
        launchButtonLabel: "Assistant",
        locale: getUserLocale(),
        auth: {
          ["access_token"]: c3Context.accessToken
        }
      };
    };

    loadAiAssistantScript(remoteEntryUrl)
      .then(() => customElements.whenDefined(AI_ASSISTANT_ELEMENT_NAME))
      .then(applyConfig)
      .catch((error) => {
        logger.error(error);
      });
  }, [
    c3Context,
    c3Context.accessToken,
    c3Context.aiAssistantBaseUrl,
    aiAssistantTheme,
    isConfigValid,
    remoteEntryUrl
  ]);

  return (
    <>
      {isConfigValid &&
        createElement(AI_ASSISTANT_ELEMENT_NAME, {
          ref
        })}
    </>
  );
}
